import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureSupabaseError } from '../lib/supabaseError';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { CommsBackend } from '../lib/config';
import { useFindOrCreateConversation } from './useFindOrCreateConversation';

interface WorkerBatchResponse {
  success: boolean;
  data: Array<{
    id: number;
    comms_backend?: CommsBackend;
    user?: { id: number } | null;
  }>;
}

interface OpenChatV2Params {
  // One of workerId / userId is required. userId callers (the v1 chat
  // search returns user ids) resolve the worker id via the same batch
  // call that resolves the backend.
  workerId?: number;
  userId?: number;
  name: string;
  // 'blank' (default) opens a new tab — the profile-modal behavior.
  // 'self' navigates the SPA (react-router) so "open a chat" lands the
  // admin in that chat.
  target?: 'self' | 'blank';
  // Pass 'supabase' when the caller already resolved the backend (e.g. an
  // inbox row whose badge came from the chats payload) to skip the batch
  // round-trip.
  knownBackend?: CommsBackend;
}

/**
 * Routes the admin "open a chat with this worker" action to the worker's
 * actual comms backend. Supabase-cohort workers run the Supabase-only
 * stack, so opening a PubNub ChatGroup for them silently drops the message
 * — their app never subscribes to PubNub. This resolves the worker's
 * backend from graphy and, for Supabase workers, opens the Supabase
 * conversation instead.
 *
 * Fail-safe: every error path returns `false` so the caller runs its
 * existing PubNub flow — a graphy hiccup never dead-ends a click.
 *
 * `openChatV2IfCohort` returns `true` when it opened a Supabase
 * conversation (the caller should stop) and `false` otherwise.
 */
export function useWorkerChatRouter() {
  const { open: openSupabaseConversation } = useFindOrCreateConversation();
  const navigate = useNavigate();

  const openChatV2IfCohort = useCallback(
    async (params: OpenChatV2Params): Promise<boolean> => {
      const { workerId, userId, name, target = 'blank', knownBackend } = params;
      if (workerId == null && userId == null) return false;

      let backend: CommsBackend | undefined = knownBackend;
      let resolvedWorkerId = workerId;

      if (backend !== 'supabase' || resolvedWorkerId == null) {
        try {
          const query =
            workerId != null ? { ids: [workerId] } : { user_ids: [userId] };
          const res = await axiosRubyClient.get<WorkerBatchResponse>(
            '/api/admin/workers/batch',
            { params: query },
          );
          const chip =
            workerId != null
              ? res.data.data?.find((w) => w.id === workerId)
              : res.data.data?.find((w) => w.user?.id === userId);
          if (!chip) return false;
          resolvedWorkerId = chip.id;
          backend = chip.comms_backend === 'supabase' ? 'supabase' : 'pubnub';
        } catch (err: unknown) {
          captureSupabaseError(err, {
            action: 'resolve_worker_backend',
            extra: { worker_id: workerId, user_id: userId },
          });
          return false;
        }
      }

      if (backend !== 'supabase' || resolvedWorkerId == null) return false;

      try {
        const conversationId = await openSupabaseConversation(
          resolvedWorkerId,
          name,
        );
        // /inbox (CommsAgencyChat) is the only surface that reads
        // conversation_id.
        if (target === 'self') {
          navigate(`/inbox?conversation_id=${conversationId}`);
        } else {
          window.open(`/inbox?conversation_id=${conversationId}`, '_blank');
        }
        return true;
      } catch {
        // Sentry capture happens inside useFindOrCreateConversation. A
        // dead-but-visible PubNub chat beats a dead-end click.
        return false;
      }
    },
    [openSupabaseConversation, navigate],
  );

  return { openChatV2IfCohort };
}
